<script>
export default {
  name: 'OrderSummaryCard',
  
  props: {
    eventName: {
      type: String,
      default: '',
    },
    location: {
      type: String,
      default: '',
    },
    lockerName: {
      type: String,
      default: '',
    },
    dates: {
      type: Array,
      default: () => [],
    },
    quantity: {
      type: Number,
      default: 1,
    },
    subtotal: {
      type: Number,
      default: 0,
    },
    tax: {
      type: Number,
      default: 0,
    },
    total: {
      type: Number,
      default: 0,
    },
    serviceFee: {
      type: Number,
      default: 0,
    },
    showRate: {
      type: Boolean,
      default: false,
    },
    rate: {
      type: Number,
      default: 0,
    },
    rateLabel: {
      type: String,
      default: 'Rate',
    },
    collapsible: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isCollapsed: true,
      isMobile: false,
    }
  },

  computed: {
    datesDisplay() {
      if (!this.dates || this.dates.length === 0) return 'No dates'
      if (this.dates.length === 1) return '1 day'
      return `${this.dates.length} days`
    },

    dateRangeDisplay() {
      if (!this.dates || this.dates.length === 0) return ''
      const sorted = [...this.dates].sort()
      const first = new Date(sorted[0] + 'T00:00:00')
      const last = new Date(sorted[sorted.length - 1] + 'T00:00:00')
      const opts = { month: 'short', day: 'numeric' }
      if (sorted.length === 1) {
        return first.toLocaleDateString('en-US', opts)
      }
      return `${first.toLocaleDateString('en-US', opts)} - ${last.toLocaleDateString('en-US', opts)}`
    },

    collapsedSummary() {
      return `${this.quantity}× ${this.lockerName || 'Locker'}`
    },
  },

  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
  },

  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth < 769
    },

    toggleCollapse() {
      if (this.collapsible && this.isMobile) {
        this.isCollapsed = !this.isCollapsed
      }
    },
  },
}
</script>

<template>
  <div 
    class="order-summary-card card"
    :class="{ 'is-collapsed': isCollapsed && isMobile && collapsible }"
  >
    <!-- Collapsed header (mobile only) -->
    <div 
      v-if="isMobile && collapsible" 
      class="summary-header"
      @click="toggleCollapse"
    >
      <div class="summary-header__content">
        <span class="summary-header__item">{{ collapsedSummary }}</span>
        <span class="summary-header__total">${{ total.toFixed(2) }}</span>
      </div>
      <b-icon :icon="isCollapsed ? 'chevron-down' : 'chevron-up'" size="is-small" />
    </div>

    <!-- Full summary body -->
    <div 
      v-show="!isCollapsed || !isMobile || !collapsible" 
      class="card-content summary-body"
    >
      <p class="title is-6 mb-3">Order Summary</p>

      <p v-if="eventName" class="is-size-7 mb-1">
        <span class="has-text-grey">Event:</span>
        {{ eventName }}
      </p>
      <p v-if="location" class="is-size-7 mb-1">
        <span class="has-text-grey">Location:</span>
        {{ location }}
      </p>
      <p v-if="lockerName" class="is-size-7 mb-1">
        <span class="has-text-grey">Locker:</span>
        {{ lockerName }}
      </p>
      <p class="is-size-7 mb-1">
        <span class="has-text-grey">Dates:</span>
        {{ datesDisplay }}
        <span v-if="dateRangeDisplay" class="has-text-grey-dark ml-1">({{ dateRangeDisplay }})</span>
      </p>
      <p class="is-size-7 mb-3">
        <span class="has-text-grey">Quantity:</span>
        {{ quantity }}
      </p>

      <hr />

      <div v-if="showRate" class="summary-line">
        <span class="label">{{ rateLabel }}</span>
        <span class="value">${{ rate.toFixed(2) }}</span>
      </div>

      <div class="summary-line">
        <span class="label">Subtotal</span>
        <span class="value">${{ subtotal.toFixed(2) }}</span>
      </div>

      <div v-if="tax > 0" class="summary-line">
        <span class="label">Tax</span>
        <span class="value">${{ tax.toFixed(2) }}</span>
      </div>

      <hr />

      <div class="summary-line summary-line--total">
        <span class="label">Total</span>
        <span class="value">${{ total.toFixed(2) }}</span>
      </div>

      <slot></slot>
    </div>
  </div>
</template>

<style scoped>
.order-summary-card {
  border-radius: 12px;
}

.summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #e5e7eb;
}

.summary-header__content {
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
  margin-right: 0.5rem;
}

.summary-header__item {
  font-weight: 500;
  font-size: 0.9rem;
}

.summary-header__total {
  font-weight: 600;
  font-size: 1rem;
}

.summary-line {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

.summary-line--total {
  font-weight: 600;
  font-size: 0.9rem;
}

.summary-line .label {
  color: #6b7280;
}

.order-summary-card.is-collapsed .summary-body {
  display: none;
}
</style>
